import { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { STATUS_LABEL } from "@/components/TaskBadges";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, authFileUrl, uploadFiles, variantWidth } from "@/lib/api";
import { useAuth, canLeadTasks } from "@/context/AuthContext";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { formatDate, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, RotateCcw, X, ImagePlus, Loader2, MapPin, Lock, CalendarDays, ArrowRight, Camera } from "lucide-react";
import { usePhotoInputs, PhotoUploadButton } from "@/components/shared/PhotoPicker";
import { toast } from "sonner";
import { Linkify } from "@/components/shared/Linkify";
import { mentionIds } from "@/components/shared/Mentions";
import { Avatar } from "@/components/shared/Avatar";
import { closesWithoutApproval } from "@/lib/tasks";
import { TaskDefects, nz, beforeAfterSequence } from "@/components/defects/TaskDefects";
import { PhotoPinViewer } from "@/components/defects/PhotoPinViewer";
import { DefectTaskForm } from "@/components/defects/DefectTaskForm";
import { useAbsenceMap, AbsenceBadge } from "@/components/shared/PeoplePicker";
import { isTypingTarget, Kbd } from "@/components/shared/ListNav";
import { ROLE_LABEL } from "@/lib/taskCalendar";
import { ProjectPill } from "@/components/tasks/TaskTable";
import { StatusIcon, PrioIcon, PropRow, PropPicker, OptionList, TYPE_ICON, TYPE_LABEL } from "@/components/tasks/TaskProps";
import { TaskActivity } from "@/components/tasks/TaskActivity";
import { ask } from "@/lib/confirm";
import { ApproveButton } from "@/components/shared/ApproveButton";
import { celebrate } from "@/lib/celebrate";

const daysWord = (n) => (n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní");
const dayDiff = (deadline) => {
  const d = new Date((deadline || "").slice(0, 10) + "T00:00:00");
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
};
const DOW = ["ne", "po", "út", "st", "čt", "pá", "so"];
const ymdLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (n) => { const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() + n); return d; };
const shortName = (n) => { const w = (n || "").split(" "); return w.length > 1 ? `${w[0]} ${w[1][0]}.` : n; };
// 362: posun jen uvnitř panelu s detailem. scrollIntoView posouvá i všechno nad ním — stránku pod panelem a v náhledu
// Emergentu i celé okno aplikace, které pak zůstane useknuté a zpátky posunout nejde.
function scrollInPanel(el, smooth) {
  let sc = el.parentElement;
  while (sc && !(/(auto|scroll)/.test(getComputedStyle(sc).overflowY) && sc.scrollHeight > sc.clientHeight)) sc = sc.parentElement;
  if (!sc || sc === document.scrollingElement || sc === document.body) return;
  const r = el.getBoundingClientRect(), box = sc.getBoundingClientRect();
  sc.scrollTo?.({ top: sc.scrollTop + r.top - box.top - (sc.clientHeight - r.height) / 2, behavior: smooth ? "smooth" : "auto" });
}
const PRIOS = ["Vysoká", "Střední", "Nízká"];
const KEY_PROP = { s: "status", p: "prio", d: "due", a: "who", z: "proj", t: "type" };

// 361: název a popis se upravují přímo na místě (klik, Enter / ⌘↵ uloží, Esc vrátí) — dřív okno Upravit.
function InlineText({ value, editable, multiline, placeholder, onSave, className = "", testid, render }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value || "");
  const ref = useRef(null);
  useEffect(() => { if (!edit) setV(value || ""); }, [value, edit]);
  useLayoutEffect(() => { const t = ref.current; if (edit && t) { t.style.height = "auto"; t.style.height = `${t.scrollHeight}px`; } }, [edit, v]);
  if (!edit) {
    const body = value ? (render ? render(value) : value) : <span className="text-muted-foreground font-normal">{placeholder}</span>;
    if (!editable) return value ? <div className={className} data-testid={testid}>{body}</div> : null;
    return (
      <div role="button" tabIndex={0} title="Upravit" data-testid={testid}
        onClick={(e) => { if (!e.target.closest("a")) setEdit(true); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setEdit(true); } }}
        className={`${className} -mx-2 px-2 py-0.5 rounded-[8px] hover:bg-secondary/60 cursor-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40`}>{body}</div>
    );
  }
  const save = () => {
    setEdit(false);
    const nv = v.trim();
    if (!multiline && !nv) { toast.error("Úkol musí mít název."); return; }
    if (nv !== (value || "").trim()) onSave(nv);
  };
  return (
    <textarea ref={ref} autoFocus value={v} rows={1} onChange={(e) => setV(e.target.value)} onBlur={save} placeholder={placeholder} data-testid={`${testid}-input`} data-inline-edit="true"
      onKeyDown={(e) => {
        if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setV(value || ""); setEdit(false); }
        else if (e.key === "Enter" && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.blur(); }
      }}
      className={`${className} block -mx-2 px-2 py-0.5 w-[calc(100%+1rem)] rounded-[8px] bg-secondary/60 outline-none resize-none ring-2 ring-brand/30`} />
  );
}

// 357: headerSlot (panel zprava na počítači) — potvrzení úkolu vlevo nahoře v hlavičce panelu jako v laboratoři
// (Schválit + ikona Vrátit, řešitel Označit jako hotové); po potvrzení zezelená a vyletí konfety.
// 361: vzhled podle Linearu — název a popis na místě, vlastnosti Stav · Priorita · Termín · Kdo · Zakázka · Typ · Zadal
// s výběrem na místě, fotky z dokončení, Aktivita (komentáře + historie, nejnovější nahoře).
// keys = klávesy S P D A Z T C ⌘↵ (jen na počítači): "panel" = detail v okně zprava, "dock" = vedle tabulky.
export default function TaskDetailView({ id, onChanged, onClose, initialAction, headerSlot = null, keys = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const manager = canLeadTasks(user);   // vedení i projektant (173) — schvalování úkolů
  const [task, setTask] = useState(null);
  const [comment, setComment] = useState("");
  const [picked, setPicked] = useState({});   // 172: označení lidé {id: jméno}
  const [cmtBusy, guardCmt] = useSubmitGuard();     // komentář
  const [cplBusy, guardCpl] = useSubmitGuard();     // dokončení úkolu
  const [completeOpen, setCompleteOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnText, setReturnText] = useState("");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [upProg, setUpProg] = useState(null);        // { done, total } během nahrávání
  // Čítač místo boolean: dragleave chodí při přejezdu přes vnitřní prvky,
  // s pouhým true/false by overlay blikal.
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  // 363: fotky přetažené / vložené do detailu rozpracovaného úkolu (task.photos) — tah z téhle stránky překryv neukáže
  const innerDrag = useRef(false);
  const attachRef = useRef(null);
  const canAttachRef = useRef(false);
  const completeNowRef = useRef(null);
  const [pick, setPick] = useState(null);       // otevřený výběr vlastnosti: status | prio | due | who | proj | type
  const [lists, setLists] = useState(null);     // zakázky a tým pro výběry (načtou se při prvním otevření)
  const [whoSel, setWhoSel] = useState(null);   // Kdo: rozpracovaný výběr — uloží se jednou po zavření
  const rootRef = useRef(null);
  // Závady ze špendlíků (routes/defects.py): uložené + rozpracované nové.
  const [defects, setDefects] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [viewer, setViewer] = useState(null);   // { photos, start, focusDraft }
  // 178: špendlíky z fotek úkolu → vrátit k opravě v TOMTO úkolu, nebo NOVÝ úkol.
  const [defMode, setDefMode] = useState(null);  // null = podle stavu úkolu
  const [defFlash, setDefFlash] = useState(false);
  const defRef = useRef(null);
  const actionsRef = useRef(null);
  const doneBtn = useRef(null);   // 357: odtud vyletí konfety po dokončení
  const [defBusy, guardDef] = useSubmitGuard();
  const photoPick = usePhotoInputs({ onChange: (e) => pickPhotos(e), disabled: uploading });

  // 218: úkol a jeho závady souběžně (dřív závady až po úkolu = o jeden request déle)
  const load = useCallback(async () => {
    const defs = api.get(`/tasks/${id}/defects`).then((r) => r.data || []).catch(() => []);
    try {
      const { data } = await api.get(`/tasks/${id}`); setTask(data);
    } catch { toast.error("Úkol nenalezen."); onClose?.(); return; }
    setDefects(await defs);
  }, [id]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); };

  // Tah po Kanbanu do „Ke schválení" otevře rovnou dialog Dokončení. Ref =
  // jen jednou, ať se okno po odeslání (a dalším load) samo znovu neotvírá.
  const didInit = useRef(false);
  // Detail otevřený jen kvůli dokončení (tah doprava / kolečko) se má po zavření
  // dialogu vrátit do seznamu, ne zůstat na detailu, který si nikdo nevyžádal.
  const openedForComplete = useRef(initialAction === "complete");
  useEffect(() => {
    if (didInit.current || initialAction !== "complete" || !task) return;
    didInit.current = true;
    if (["Hotovo", "Schváleno"].includes(task.status)) return;
    if (closesWithoutApproval(task, user)) completeNowRef.current?.(doneBtn.current);   // 363: zavírá rovnou, bez okna
    else setCompleteOpen(true);
  }, [initialAction, task]); // eslint-disable-line

  // Jeden vstup pro výběr souborů, přetažení myší i Cmd+V. uploadFile řeší
  // zmenšení, HEIC hlášku i macOS práva (viz lib/api.js); průběh hlásíme po
  // fotkách, ať pracovník vidí, že se něco děje, a dialog nezavírá.
  // 363: toTask = k fotkám rozpracovaného úkolu v detailu (jinak do okna dokončení)
  const addFiles = useCallback(async (list, toTask = false) => {
    const all = [...(list || [])];
    const files = all.filter((f) => f?.type?.startsWith("image/") || /\.(heic|heif)$/i.test(f?.name || ""));
    if (!files.length) { if (all.length) toast.error("Sem patří jen fotky."); return; }
    setUploading(true); setUpProg({ done: 0, total: files.length });
    const got = [];
    try {
      // 257: souběžně s opakováním; každá hotová fotka hned naskočí, chyba jedné nezastaví další
      const { failed } = await uploadFiles(files, {
        onResult: (r) => { if (!r.ok) return; if (toTask) got.push(r.url); else setPhotos((p) => [...p, r.url]); },
        onEach: (done, total) => setUpProg({ done, total }),
      });
      if (failed.length) toast.error(`Nenahrálo se ${failed.length} z ${files.length}: ${formatApiError(failed[0].error?.response?.data?.detail) || "zkus to znovu"}`);
      if (got.length) await attachRef.current?.(got);
    } finally { setUploading(false); setUpProg(null); }
  }, []);
  const pickPhotos = (e) => { const list = [...(e.target.files || [])]; e.target.value = ""; addFiles(list, !completeOpen); };

  // Cmd+V: screenshot nebo zkopírovaná fotka rovnou do dokončení úkolu; 363: bez okna k fotkám rozpracovaného úkolu
  // (fotka zkopírovaná z e-mailu nebo WhatsAppu v prohlížeči — pravé tlačítko › Kopírovat obrázek).
  useEffect(() => {
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])];
      if (!files.length) return;
      if (completeOpen) { e.preventDefault(); addFiles(files); return; }
      const imgs = files.filter((f) => f.type?.startsWith("image/"));
      // 364: nad detailem je jiné okno (Nový úkol, …) — fotka patří tam, ne k tomuto úkolu
      const foreign = [...document.querySelectorAll('[role="dialog"]')].some((d) => !d.contains(rootRef.current));
      if (imgs.length && canAttachRef.current && !foreign) { e.preventDefault(); addFiles(imgs, true); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [completeOpen, addFiles]);
  useEffect(() => {
    const on = () => { innerDrag.current = true; };
    const off = () => { innerDrag.current = false; };
    document.addEventListener("dragstart", on); document.addEventListener("dragend", off); document.addEventListener("drop", off);
    return () => { document.removeEventListener("dragstart", on); document.removeEventListener("dragend", off); document.removeEventListener("drop", off); };
  }, []);

  // 361: nepřítomnosti pro výběr Kdo (v okně termínu úkolu), načtou se až při otevření výběru
  const absences = useAbsenceMap(null, (task?.deadline || "").slice(0, 10) || null, pick === "who");
  // Kdo se ukládá jednou, když se výběr zavře (ne za každé zaškrtnutí — v historii by byl zmatek)
  const whoRef = useRef(null);
  whoRef.current = { whoSel, task, team: lists?.team };
  useEffect(() => {
    if (pick === "who") return;
    const { whoSel: sel, task: t } = whoRef.current;
    if (!sel || !t) return;
    setWhoSel(null);
    const before = t.assignees || [];
    if (sel.length === before.length && sel.every((x) => before.includes(x))) return;
    const people = whoRef.current.team || [];
    patchRef.current?.({ assignees: sel }, { assignee_users: sel.map((x) => people.find((u) => u.id === x) || (t.assignee_users || []).find((u) => u.id === x) || { id: x, name: "" }) });
  }, [pick]);
  const patchRef = useRef(null);
  const keysRef = useRef(null);
  useEffect(() => {
    if (!keys) return undefined;
    const allowed = keys === "panel" ? 1 : 0;   // okno detailu samo je dialog
    const h = (e) => {
      if (e.altKey || e.defaultPrevented) return;
      if (document.querySelectorAll('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]').length > allowed) return;
      const r = keysRef.current; if (!r) return;
      if (e.metaKey || e.ctrlKey) { if (e.key === "Enter" && !isTypingTarget(e.target)) { e.preventDefault(); r.primary(); } return; }
      if (isTypingTarget(e.target)) return;
      const k = e.key.toLowerCase();
      if (KEY_PROP[k]) { e.preventDefault(); r.open(KEY_PROP[k]); }
      else if (k === "c") { e.preventDefault(); r.comment(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [keys]);

  if (!task) return <div className="text-muted-foreground p-6">Načítám…</div>;

  const isAssignee = (task.assignees || []).includes(user.id);
  const done = ["Hotovo", "Schváleno"].includes(task.status);
  const active = !done;
  const diff = task.deadline ? dayDiff(task.deadline) : null;
  const overdue = diff !== null && diff < 0 && !done;
  // 276: upravit smí i řešitel (kromě dělníka a subdodavatele); přeřadit lidi jen autor nebo vedení.
  const canEdit = task.status !== "Schváleno" && (task.created_by === user.id || user.role === "OWNER"
    || (isAssignee && !["WORKER", "SUBCONTRACTOR"].includes(user.role)));
  // 361: kdo co smí — zrcadlí tasks.py:update_task (zadání / třídění / lidé; schválený úkol nikdo)
  const editor = isAssignee && !["WORKER", "SUBCONTRACTOR"].includes(user.role);
  const canTriage = task.status !== "Schváleno" && (task.created_by === user.id || manager || editor);
  const canReassign = task.status !== "Schváleno" && (task.created_by === user.id || manager);
  const canReopen = manager || task.completion?.by === user.id || isAssignee;
  const roWhy = task.status === "Schváleno" ? "Hotový úkol se už nemění" : "Mění autor úkolu, řešitel nebo vedení";
  // 191b: soukromý úkol (jen pro sebe) vidí jen autor; sdílet s vedením může jen on.
  const canPrivacy = task.created_by === user.id && (task.assignees || []).length > 0 && task.assignees.every((a) => a === task.created_by);
  const togglePrivate = async () => {
    try {
      await api.patch(`/tasks/${id}`, { private: !task.private });
      toast.success(task.private ? "Úkol teď uvidí i vedení firmy." : "Úkol je soukromý — vidíš ho jen ty.");
      changed();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // 91: kdo sám schvaluje (nebo dělá vlastní úkol), zavírá rovnou — bez fronty.
  const closesNow = closesWithoutApproval(task, user);

  // 361: úprava jedné vlastnosti hned (optimisticky), local = co se má ukázat navíc (název zakázky, lidé)
  const patch = async (body, local = {}) => {
    const prev = task;
    setTask((t) => (t ? { ...t, ...body, ...local } : t));
    try { await api.patch(`/tasks/${id}`, body); changed(); }
    catch (e) { setTask(prev); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  patchRef.current = patch;
  const loadLists = () => {
    if (lists) return;
    Promise.all([api.get("/projects/options"), api.get("/team")])
      .then(([p, t]) => setLists({ projects: p.data || [], team: (t.data || []).filter((u) => u.status !== "inactive" && u.role !== "ACCOUNTANT") }))
      .catch(() => setLists({ projects: [], team: [] }));
  };
  // 363: fotky k úkolu přidané v detailu (přetažením, Cmd+V, tlačítkem) — při dokončení přejdou do completion
  // úkol se závadami jde přes opravy (fotka u každé závady) — do detailu se fotky nepřidávají
  const canAttach = active && !defects.some((d) => d.status === "open" || d.status === "fixed");
  canAttachRef.current = canAttach;
  const attached = task.photos || [];
  attachRef.current = async (urls) => {
    try { const { data } = await api.post(`/tasks/${id}/photos`, { photos: urls }); setTask(data); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removePhoto = async (url) => {
    setTask((t) => (t ? { ...t, photos: (t.photos || []).filter((u) => u !== url) } : t));
    try { await api.delete(`/tasks/${id}/photos`, { params: { url } }); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const fromOutside = (dt) => { const ty = [...(dt?.types || [])]; return ty.includes("Files") || (!innerDrag.current && ty.includes("text/uri-list")); };
  // Soubor ze složky / Mailu / WhatsApp Desktop přijde jako soubor. Fotka z webu (Gmail, WhatsApp Web) jen jako odkaz —
  // zkusíme ji stáhnout; když to prohlížeč nedovolí, poradíme Kopírovat obrázek + Cmd+V.
  const dropToTask = async (dt) => {
    const files = [...(dt?.files || [])];
    if (files.length) { addFiles(files, true); return; }
    const url = (dt?.getData?.("text/uri-list") || "").split(/\r?\n/).find((l) => l && !l.startsWith("#"));
    try {
      if (!url || !/^(https?|data):/.test(url)) throw new Error("odkaz");
      const blob = await (await fetch(url)).blob();
      if (!blob.type.startsWith("image/")) throw new Error("typ");
      addFiles([new File([blob], `foto.${blob.type.split("/")[1] || "jpg"}`, { type: blob.type })], true);
    } catch {
      toast.info("Tuhle fotku prohlížeč přetáhnout nedovolí. Klikni na ni pravým tlačítkem › Kopírovat obrázek a tady dej Cmd+V — nebo ji ulož a přetáhni ze složky.", { duration: 8000 });
    }
  };
  const dropProps = canAttach && !completeOpen ? {
    onDragEnter: (e) => { if (!fromOutside(e.dataTransfer)) return; e.preventDefault(); dragDepth.current += 1; setDragOver(true); },
    onDragOver: (e) => { if (fromOutside(e.dataTransfer)) e.preventDefault(); },
    onDragLeave: () => { if (!dragDepth.current) return; dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragOver(false); } },
    onDrop: (e) => { if (!fromOutside(e.dataTransfer)) return; e.preventDefault(); dragDepth.current = 0; setDragOver(false); dropToTask(e.dataTransfer); },
  } : {};
  // 363: kdo úkol zavírá sám (vedení, vlastní úkol), dokončí ho bez okna — fotky už leží v detailu, poznámka patří do komentáře.
  // Okno zůstává jen pro Odeslat ke schválení (poznámka je tam povinná).
  const completeNow = async (from, quiet = false) => {
    const prev = task, at = new Date().toISOString();
    setTask((t) => (t ? { ...t, status: "Schváleno", photos: undefined, completion: { note: "", photos: t.photos || [], by: user.id, by_name: user.name, at },
      history: [...(t.history || []), { id: `opt-${Date.now()}`, action: "completed", user_name: user.name, at }] } : t));
    try {
      await api.post(`/tasks/${id}/complete`, { note: "", photos: [] });
      celebrate(from);
      if (!quiet) toast.success("Úkol hotový.");
      changed();
      if (openedForComplete.current) onClose?.();
    } catch (e) { setTask(prev); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  completeNowRef.current = (from) => guardCpl(() => completeNow(from));
  const startComplete = (from, quiet) => (closesNow ? guardCpl(() => completeNow(from, quiet)) : setCompleteOpen(true));
  const submitComplete = async () => {
    if (!closesNow && !note.trim()) return toast.error("Vyplňte poznámku k dokončení.");
    try {
      await api.post(`/tasks/${id}/complete`, { note, photos });
      toast.success(closesNow ? "Úkol hotový." : "Úkol odeslán ke schválení."); setCompleteOpen(false); setNote(""); setPhotos([]); changed();
      celebrate(doneBtn.current);
      if (openedForComplete.current) onClose?.();   // po odškrtnutí zpět do seznamu
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // 357: konfety z tlačítka, kterým se schvalovalo; z hlavičky panelu bez bubliny (zakryla by zelené „Schváleno“)
  const approve = async (e, quiet = false) => {
    const from = e?.currentTarget;
    try {
      await api.post(`/tasks/${id}/approve`);
      setTask((t) => (t ? { ...t, status: "Schváleno", history: [...(t.history || []), { id: `opt-${Date.now()}`, action: "approved", user_name: user.name, at: new Date().toISOString() }] } : t));
      celebrate(from);
      if (!quiet) toast.success("Úkol schválen.");
      changed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  // 358: schváleno omylem — druhé klepnutí na „Schváleno" vrátí úkol ke schválení (jako v Asaně)
  const unapprove = async () => {
    try {
      await api.post(`/tasks/${id}/unapprove`);
      setTask((t) => (t ? { ...t, status: "Hotovo", history: [...(t.history || []), { id: `opt-${Date.now()}`, action: "unapproved", user_name: user.name, at: new Date().toISOString() }] } : t));
      changed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const returnWithDefects = async () => {
    if (drafts.some((d) => !d.text.trim())) return toast.error("Doplňte popis u každé závady.");
    try {
      const { data } = await api.post(`/tasks/${id}/defects/return`, { defects: drafts });
      toast.success(`Vráceno k opravě · ${data.open} ${nz(data.open)}.`); setDrafts([]); changed();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resubmitFixes = async () => {
    try { await api.post(`/tasks/${id}/defects/resubmit`); toast.success("Opravy odeslány ke schválení."); changed(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReturn = async () => {
    if (!returnText.trim()) return toast.error("Napište důvod vrácení.");
    await api.post(`/tasks/${id}/return`, { comment: returnText }); toast.success("Úkol vrácen."); setReturnOpen(false); setReturnText(""); changed();
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/tasks/${id}/comments`, { text: comment, mentions: mentionIds(comment, picked) });
    // Komentáře seznamy úkolů nenesou (218) — stačí obnovit detail, ne celý seznam rodiče.
    setComment(""); setPicked({}); load();
  };
  const loadMentionable = () => api.get(`/tasks/${id}/mentionable`).then((r) => r.data);
  const remove = async () => {
    const extra = task.completion ? "\n\nÚkol už někdo dokončil — smažeš i jeho popis a fotky." : "";
    if (!await ask(`Opravdu smazat úkol „${task.title}“?${extra}`)) return;
    await api.delete(`/tasks/${id}`); toast.success("Úkol smazán."); onChanged?.(); onClose?.();
  };

  const rel = diff === null ? "" : diff === 0 ? "dnes" : diff === -1 ? "včera" : diff === 1 ? "zítra" : diff < 0 ? `před ${-diff} dny` : `za ${diff} ${daysWord(diff)}`;
  const assigneeUsers = task.assignee_users || [];
  const goProject = () => { onClose?.(); navigate(`/zakazky/${task.project_id}`); };
  const createdByName = task.created_by_name || assigneeUsers.find((u) => u.id === task.created_by)?.name || (task.created_by === user.id ? user.name : null);
  const showComplete = isAssignee && !done;
  const showApproval = manager && task.status === "Hotovo";
  const openDef = defects.filter((d) => d.status === "open").length;
  const fixedDef = defects.filter((d) => d.status === "fixed").length;
  const hasActiveDefects = openDef + fixedDef > 0;
  // Špendlíky: při kontrole úkolu ke schválení i u už schváleného (pak jen nový úkol).
  // 364: i na fotkách rozpracovaného úkolu (z detailu) — špendlíky pak dají nový úkol na opravu
  const pinBase = done ? (task.completion?.photos || []) : attached;
  const canPin = manager && pinBase.length > 0;
  const mode = defMode || (showApproval ? "return" : "new");
  const retDrafts = mode === "return" ? drafts : [];
  const pinDone = () => {
    setViewer(null);
    setTimeout(() => {
      const el = mode === "new" ? defRef.current : actionsRef.current;
      if (el) scrollInPanel(el, true);
      setDefFlash(true); setTimeout(() => setDefFlash(false), 1600);
    }, 260);
  };
  const reviewPhotos = task.completion?.photos || [];
  const openViewer = (photos, start = 0, focusDraft = null) => photos.length && setViewer({ photos, start, focusDraft });
  // Uložená závada může být i na fotce, která už v dokončení není (další kolo) — přidáme ji.
  const pinPhotos = [...new Set([...pinBase, ...defects.map((d) => d.photo_url), ...drafts.map((d) => d.photo_url)])];
  // 189: fotky opravy jdou v prohlížeči hned za fotkou „před“ své závady — mezi před
  // a po se jen přetáhne prstem, nic se nezavírá a znovu neotevírá.
  const viewPhotos = beforeAfterSequence(pinPhotos, defects);
  const when = (iso) => (iso ? new Date(iso).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
  const photoMeta = (url) => {
    const fixed = defects.find((d) => (d.fix_photos || []).includes(url));
    if (fixed) return { author: fixed.fixed_by_name, time: when(fixed.fixed_at), src: `po opravě · závada ${fixed.n}` };
    const on = defects.filter((d) => d.photo_url === url).map((d) => d.n);
    const by = task.completion?.by_name;
    if (on.length) return { author: by, time: when(task.completion?.at), src: `před · ${on.length === 1 ? "závada" : "závady"} ${on.join(", ")}` };
    return reviewPhotos.includes(url) ? { author: by, time: when(task.completion?.at), src: "dokončení úkolu" } : null;
  };
  const showDefect = (d) => openViewer(viewPhotos, Math.max(0, viewPhotos.indexOf(d.photo_url)));
  const showDraft = (k) => openViewer(viewPhotos, Math.max(0, viewPhotos.indexOf(drafts[k].photo_url)), k);
  const returnCount = retDrafts.length + openDef;
  const canDelete = task.created_by === user.id || user.role === "OWNER";
  const primaryActions = (ids) => showComplete && hasActiveDefects ? (
    <div className="space-y-1.5">
      <Button onClick={() => guardDef(resubmitFixes)} disabled={defBusy || openDef > 0 || fixedDef === 0} {...(ids ? { "data-testid": "defects-resubmit" } : {})}
        className="w-full md:w-auto md:px-8 h-[52px] rounded-full bg-success hover:bg-success/90 text-white font-extrabold text-base">
        <CheckCircle2 size={18} /> Odeslat opravy ke schválení
      </Button>
      {openDef > 0 && <p className="text-[12.5px] text-muted-foreground">Nejdřív oprav a vyfoť všechny závady ({openDef} zbývá).</p>}
    </div>
  ) : showApproval && (returnCount > 0 || fixedDef > 0) ? (
    <div className="space-y-1.5">
      {fixedDef > 0 ? (
        <Button disabled className="w-full md:w-auto md:px-8 h-[52px] rounded-full font-extrabold"><CheckCircle2 size={18} /> Schválit úkol</Button>
      ) : (
        <Button onClick={() => guardDef(returnWithDefects)} disabled={defBusy || retDrafts.some((d) => !d.text.trim())} {...(ids ? { "data-testid": "defects-return" } : {})}
          className="w-full md:w-auto md:px-8 h-[52px] rounded-full bg-brand hover:bg-brand/90 text-white font-extrabold text-base">
          <RotateCcw size={18} /> Vrátit k opravě · {returnCount} {nz(returnCount)}
        </Button>
      )}
      <p className="text-[12.5px] text-muted-foreground">
        {fixedDef > 0 ? `U každé opravené závady zvolte Převzít, nebo Není opraveno (${fixedDef} zbývá).`
          : retDrafts.some((d) => !d.text.trim()) ? "Doplňte popis u každé nové závady."
          : "Do komentářů se zapíše jen řádek „Vráceno k opravě“ — řešiteli přijde jedno upozornění."}
      </p>
      {fixedDef === 0 && retDrafts.length > 0 && (
        <button type="button" onClick={() => setDefMode("new")} {...(ids ? { "data-testid": "defects-as-new-task" } : {})}
          className="text-[13px] font-bold text-brand hover:underline">…nebo z nových závad udělat samostatný úkol</button>
      )}
    </div>
  ) : showComplete ? (
    <Button onClick={(e) => startComplete(e.currentTarget)} {...(ids ? { "data-testid": "complete-task-btn" } : {})} className="w-full h-[52px] rounded-[12px] bg-success hover:bg-success/90 text-white font-bold text-base">
      <CheckCircle2 size={18} /> {closesNow ? "Označit jako hotovo" : "Odeslat ke schválení"}
    </Button>
  ) : showApproval ? (
    <div className="flex flex-col sm:flex-row gap-2.5">
      <Button onClick={approve} {...(ids ? { "data-testid": "approve-btn" } : {})} className="flex-1 h-[52px] rounded-[12px] bg-success hover:bg-success/90 text-white font-bold"><CheckCircle2 size={18} /> {defects.length ? "Schválit úkol" : "Schválit — hotovo"}</Button>
      <Button onClick={() => setReturnOpen(true)} {...(ids ? { "data-testid": "return-btn" } : {})} variant="outline" className="flex-1 h-[52px] rounded-[12px] font-bold"><RotateCcw size={18} /> Vrátit</Button>
    </div>
  ) : null;

  // 357: v panelu potvrzení vlevo nahoře; závady a jejich vrácení zůstávají dole u fotek.
  const plainApprove = showApproval && !showComplete && returnCount === 0 && fixedDef === 0;
  const plainComplete = showComplete && !hasActiveDefects;
  const headerHandles = !!headerSlot && (plainApprove || plainComplete);
  // 358: schválení, které prošlo schvalováním, jde v panelu vrátit druhým klepnutím na „Schváleno"
  const lastAct = [...(task.history || [])].reverse().find((h) => ["approved", "completed", "submitted", "returned", "reopened", "unapproved"].includes(h.action))?.action;
  const canUnapprove = manager && task.status === "Schváleno" && lastAct === "approved";
  const hdrBtn = plainApprove ? { label: "Schválit", onClick: (e) => approve(e, true), testid: "approve-btn" }
    : plainComplete ? { label: "Označit jako hotové", onClick: (e) => startComplete(e.currentTarget, true), testid: "complete-task-btn" }
    : done ? { done: true, label: task.status === "Schváleno" ? "Schváleno" : "Hotovo", testid: "task-done-state",
      ...(canUnapprove ? { onClick: unapprove, title: "Zrušit schválení" } : {}) } : null;
  // 358: Vrátit k opravě s textem a úplně vlevo (úkol jde zpět řešiteli), vedle Schválit
  const header = headerSlot && createPortal(hdrBtn ? (<>
    {plainApprove && (
      <button type="button" onClick={() => setReturnOpen(true)} data-testid="return-btn"
        className="h-9 shrink-0 inline-flex items-center gap-1.5 px-3 rounded-[10px] border-[1.5px] border-border text-[14px] font-bold text-muted-foreground hover:text-brand hover:border-brand/60 transition-colors">
        <RotateCcw size={15} aria-hidden="true" />Vrátit k opravě
      </button>
    )}
    <ApproveButton ref={doneBtn} {...hdrBtn} />
  </>) : (   // 361: cizí rozpracovaný úkol — kdo ho řeší (místo „Detail úkolu“)
    <span className="text-sm font-semibold text-muted-foreground truncate" data-testid="task-header-who">
      {(task.assignee_users || []).length ? `Řeší ${(task.assignee_users || []).map((a) => shortName(a.name)).join(", ")}` : "Detail úkolu"}
    </span>
  ), headerSlot);

  // ---- 361: výběry vlastností (Stav · Priorita · Termín · Kdo · Zakázka · Typ)
  const openPick = (k) => {
    if (k !== "status" && !(k === "who" ? canReassign : k === "proj" ? canEdit : canTriage)) { toast.info(k === "who" ? "Lidi mění autor úkolu nebo vedení." : roWhy); return; }
    if (k === "who" || k === "proj") loadLists();
    if (k === "who") setWhoSel(task.assignees || []);
    setPick(k);
  };
  const onPickChange = (k) => (o) => (o ? openPick(k) : setPick(null));
  const statusWhy = (v) => {
    if (v === task.status) return "";
    if (v === "Nový" || v === "V řešení") return active || canReopen ? "" : "Vrátit může řešitel nebo vedení";
    if (v === "Hotovo") return !active ? "Úkol už je odeslaný" : !isAssignee ? "Ke schválení posílá řešitel" : closesNow ? "Tvůj úkol se zavírá rovnou — zvol Hotovo" : "";
    if (task.status === "Hotovo") return manager ? "" : "Schvaluje vedení";
    return isAssignee ? (closesNow ? "" : "Nejdřív pošli ke schválení") : manager ? "" : "Dokončí řešitel";
  };
  const pickStatus = async (o) => {
    if (o.why) { toast.info(o.why); return; }
    setPick(null);
    if (o.value === task.status) return;
    if (o.value === "Nový" || o.value === "V řešení") {
      if (active) { patch({ status: o.value }); return; }
      try { await api.post(`/tasks/${id}/reopen`, { status: o.value }); toast.success("Úkol vrácen mezi rozpracované."); changed(); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (o.value === "Hotovo") { setCompleteOpen(true); return; }   // Ke schválení = dokončení s popisem a fotkami
    if (task.status === "Hotovo") { approve({ currentTarget: doneBtn.current }, true); return; }
    if (isAssignee) { startComplete(doneBtn.current); return; }
    // vedení zavře cizí rozpracovaný úkol rovnou — jako tah do Hotovo v Kanbanu
    guardCpl(() => completeNow(doneBtn.current));
  };
  const isMob = !headerSlot;   // telefon: výběry zespodu, bez kláves
  const dueOpts = (() => {
    const dw = new Date().getDay(), toFri = ((5 - dw) + 7) % 7 || 7, toMon = ((1 - dw) + 7) % 7 || 7;
    const seen = new Set();
    return [[0, "Dnes"], [1, "Zítra"], [toFri, "Pátek"], [toMon, "Příští týden"]].filter(([n]) => !seen.has(n) && seen.add(n))
      .map(([n, l], i) => { const d = addDays(n); return { value: ymdLocal(d), label: l, sub: `${DOW[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`, key: String(i + 1), icon: <CalendarDays size={16} className="text-muted-foreground shrink-0" /> }; })
      .concat([{ value: null, label: "Bez termínu", key: "5", icon: <X size={16} className="text-muted-foreground shrink-0" /> }])
      .map((o) => ({ ...o, on: (task.deadline || "").slice(0, 10) === (o.value || "") || (!task.deadline && o.value === null) }));
  })();
  const due = (() => {
    if (!task.deadline) return <span className="text-muted-foreground font-medium">Bez termínu</span>;
    const d = new Date(task.deadline);
    return (<><CalendarDays size={16} className="text-muted-foreground shrink-0" />
      <span className={`truncate tabular-nums ${overdue ? "text-destructive" : diff === 0 && !done ? "text-amber-600 dark:text-amber-400" : ""}`}>
        {DOW[d.getDay()]} {d.getDate()}. {d.getMonth() + 1}.{task.time_from ? ` · ${task.time_from}` : ""}<span className="font-medium"> · {rel}</span>
      </span></>);
  })();
  const people = lists?.team || [];
  const whoOpts = people.map((u) => ({ value: u.id, label: u.id === user.id ? `${u.name} (já)` : u.name, sub: ROLE_LABEL[u.role] || u.role, on: (whoSel || []).includes(u.id),
    icon: <Avatar name={u.name} photo={u.photo_url} size={26} className="bg-brand/15 text-brand" />, right: <AbsenceBadge items={absences[u.id]} /> }));
  const projOpts = [{ value: null, label: "Bez zakázky", on: !task.project_id, icon: <X size={16} className="text-muted-foreground shrink-0" /> },
    ...(lists?.projects || []).map((p) => ({ value: p.id, label: p.name, on: task.project_id === p.id, icon: <ProjectPill id={p.id} name="" /> }))];
  const props = [
    { k: "status", label: "Stav", hint: "S", ro: false, value: <><StatusIcon status={task.status} />{STATUS_LABEL[task.status] || task.status}</>,
      body: <OptionList testid="pick-status" mobile={isMob} onPick={pickStatus}
        options={["Nový", "V řešení", "Hotovo", "Schváleno"].map((v, i) => ({ value: v, label: STATUS_LABEL[v], key: String(i + 1), on: task.status === v, why: statusWhy(v), icon: <StatusIcon status={v} /> }))} /> },
    { k: "prio", label: "Priorita", hint: "P", ro: !canTriage, value: <><PrioIcon value={task.priority || "Střední"} />{task.priority || "Střední"}</>,
      body: <OptionList testid="pick-prio" mobile={isMob} onPick={(o) => { setPick(null); if (o.value !== task.priority) patch({ priority: o.value }); }}
        options={PRIOS.map((v, i) => ({ value: v, label: v, key: String(i + 1), on: (task.priority || "Střední") === v, icon: <PrioIcon value={v} /> }))} /> },
    { k: "due", label: "Termín", hint: "D", ro: !canTriage, value: due,
      body: <OptionList testid="pick-due" mobile={isMob} options={dueOpts}
        onPick={(o) => { setPick(null); if ((task.deadline || "").slice(0, 10) !== (o.value || "")) patch({ deadline: o.value }); }}
        footer={<label className="flex items-center gap-2 mt-1.5 pt-2 px-1.5 border-t border-border text-xs text-muted-foreground whitespace-nowrap">Jiné datum
          <input type="date" defaultValue={(task.deadline || "").slice(0, 10)} data-testid="pick-due-date"
            onChange={(e) => { if (e.target.value) { setPick(null); patch({ deadline: e.target.value }); } }}
            className="ml-auto h-9 min-w-0 rounded-[9px] border border-border bg-card px-2 text-base md:text-sm text-foreground" /></label>} /> },
    { k: "who", label: "Kdo", hint: "A", ro: !canReassign, roTitle: "Lidi mění autor úkolu nebo vedení",
      value: assigneeUsers.length ? (<>
        <span className="flex shrink-0">{assigneeUsers.slice(0, 3).map((a, i) => <Avatar key={a.id} name={a.name} photo={a.photo_url} size={22} className={`bg-brand/15 text-brand ring-2 ring-card ${i ? "-ml-1.5" : ""}`} />)}</span>
        <span className="truncate">{assigneeUsers.length <= 2 ? assigneeUsers.map((a) => a.name).join(", ") : `${shortName(assigneeUsers[0].name)} a ${assigneeUsers.length - 1} další`}</span></>)
        : <span className="text-muted-foreground font-medium">Nikdo</span>,
      body: lists ? <OptionList testid="pick-who" mobile={isMob} multi search placeholder="Hledat člověka…" options={whoOpts}
        onPick={(o) => setWhoSel((sel) => ((sel || []).includes(o.value) ? sel.filter((x) => x !== o.value) : [...(sel || []), o.value]))}
        footer={isMob ? <Button onClick={() => setPick(null)} className="w-full h-12 mt-3 rounded-full font-bold" data-testid="pick-who-done">Hotovo</Button>
          : <p className="mt-1.5 pt-2 px-1.5 border-t border-border text-xs text-muted-foreground">Enter přidá / odebere · Esc uloží</p>} />
        : <p className="p-3 text-sm text-muted-foreground">Načítám…</p> },
    { k: "proj", label: "Zakázka", hint: "Z", ro: !canEdit, value: task.project_id ? <ProjectPill id={task.project_id} name={task.project_name} /> : <span className="text-muted-foreground font-medium">Bez zakázky</span>,
      body: lists ? <OptionList testid="pick-proj" mobile={isMob} search placeholder="Hledat zakázku…" options={projOpts}
        onPick={(o) => { setPick(null); if ((task.project_id || null) !== o.value) patch({ project_id: o.value }, { project_name: o.value ? o.label : null }); }} />
        : <p className="p-3 text-sm text-muted-foreground">Načítám…</p> },
    { k: "type", label: "Typ", hint: "T", ro: !canTriage, value: (() => { const I = TYPE_ICON[task.type] || TYPE_ICON.task; return <><I size={16} className="text-muted-foreground shrink-0" />{TYPE_LABEL[task.type] || "Úkol"}</>; })(),
      body: <OptionList testid="pick-type" mobile={isMob} onPick={(o) => { setPick(null); if (o.value !== (task.type || "task")) patch({ type: o.value }); }}
        options={["task", "call", "meeting", "email"].map((v, i) => { const I = TYPE_ICON[v]; return { value: v, label: TYPE_LABEL[v], key: String(i + 1), on: (task.type || "task") === v, icon: <I size={16} className="text-muted-foreground shrink-0" /> }; })} /> },
  ];
  const focusComment = () => {
    const el = rootRef.current?.querySelector('[data-testid="comment-input"]');
    if (!el) return;
    scrollInPanel(el, !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
    el.focus({ preventScroll: true });
  };
  keysRef.current = {
    open: (k) => (pick === k ? setPick(null) : openPick(k)),
    comment: focusComment,
    primary: () => { if (plainApprove) approve({ currentTarget: doneBtn.current }, true); else if (plainComplete) startComplete(doneBtn.current, true); },
  };
  const TypeI = TYPE_ICON[task.type] || TYPE_ICON.task;
  const donePhotos = task.completion?.photos || [];

  return (
    <div data-testid="task-detail-view" ref={rootRef} className="relative" {...dropProps}>
      {header}
      {!completeOpen && photoPick.inputs}
      {dragOver && !completeOpen && (
        <div className="absolute -inset-2 z-30 rounded-[16px] border-[3px] border-dashed border-brand bg-background/90 pointer-events-none" data-testid="detail-dropzone">
          <div className="sticky top-24 flex flex-col items-center gap-2 py-10 px-4 text-center text-brand font-extrabold text-lg">
            <ImagePlus size={44} aria-hidden="true" /> Pusťte fotky sem
            <span className="text-[13px] font-semibold text-muted-foreground">Přidají se k úkolu</span>
          </div>
        </div>
      )}
      {/* drobečky: typ · zakázka, soukromí */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2 text-[12.5px] font-semibold text-muted-foreground">
        <TypeI size={14} aria-hidden="true" /><span>{TYPE_LABEL[task.type] || "Úkol"}</span>
        {task.project_name && <><span aria-hidden="true">·</span><span className="truncate max-w-[60%]">{task.project_name}</span></>}
        {task.private && <span className="inline-flex items-center gap-1 rounded-full bg-foreground/10 text-foreground px-2 py-0.5 text-[11px] font-bold" data-testid="private-pill"><Lock size={11} /> Soukromý</span>}
        {canPrivacy && (
          <button type="button" onClick={togglePrivate} data-testid="privacy-toggle" className="ml-auto text-[12.5px] font-bold text-brand hover:underline">
            {task.private ? "Sdílet s vedením" : "Udělat soukromým"}
          </button>
        )}
      </div>

      <InlineText value={task.title} editable={canEdit} onSave={(v) => patch({ title: v })} testid="task-title"
        className="text-[22px] sm:text-[24px] font-extrabold tracking-tight leading-tight [text-wrap:balance]" />
      <div className="mt-1.5">
        <InlineText value={task.description} editable={canEdit} multiline placeholder="Přidat popis…" onSave={(v) => patch({ description: v })} testid="task-description"
          className="text-[14.5px] leading-relaxed whitespace-pre-wrap break-words" render={(v) => <Linkify text={v} />} />
      </div>

      {/* vlastnosti (Linear) */}
      <div className={`mt-4 grid ${isMob ? "" : "gap-0.5 -mx-2"}`} data-testid="task-props">
        {props.map((pr) => {
          // 364: „Otevřít zakázku“ přímo v řádku Zakázka (vpravo před klávesou), ne v řádku pod vlastnostmi
          const open = pr.k === "proj" && task.project_id;
          const picker = (
            <PropPicker key={pr.k} open={pick === pr.k} onOpenChange={onPickChange(pr.k)} mobile={isMob} title={pr.label} testid={`pick-${pr.k}-panel`}
              trigger={<PropRow label={pr.label} hint={isMob ? null : pr.hint} ro={pr.ro} roTitle={pr.roTitle || roWhy} mobile={isMob} testid={`prop-${pr.k}`}
                onClick={isMob ? () => openPick(pr.k) : undefined}>{open ? <span className="min-w-0 flex pr-[76px]">{pr.value}</span> : pr.value}</PropRow>}>
              {pr.body}
            </PropPicker>
          );
          if (!open) return picker;
          return (
            <div key={pr.k} className="relative">
              {picker}
              <button type="button" onClick={goProject} data-testid="task-project-link" title="Otevřít zakázku"
                className={`absolute top-1/2 -translate-y-1/2 ${isMob ? "right-7" : "right-10"} inline-flex items-center gap-1 h-7 px-2 rounded-full text-[12.5px] font-bold text-brand hover:bg-brand/10`}>
                Otevřít <ArrowRight size={13} aria-hidden="true" />
              </button>
            </div>
          );
        })}
        <div className={`grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 min-h-[40px] ${isMob ? "px-0.5 min-h-[50px]" : "px-2"}`} data-testid="prop-author">
          <span className="text-[13.5px] text-muted-foreground">Zadal</span>
          <span className="min-w-0 flex items-center gap-2 text-[14px] font-semibold">
            {createdByName ? <><Avatar name={createdByName} size={22} className="bg-brand/15 text-brand" /><span className="truncate">{createdByName}{task.created_at && <span className="font-medium text-muted-foreground"> · {formatDate(task.created_at)}</span>}</span></> : <span className="text-muted-foreground">—</span>}
          </span>
        </div>
      </div>
      {canAttach && !attached.length && (
        <div className="mt-1 flex items-center">
          {/* 363: fotky jdou přidat kdykoli před dokončením — tlačítkem, přetažením kamkoli do detailu nebo Cmd+V */}
          {(
            <button type="button" onClick={photoPick.open} disabled={uploading} data-testid="add-task-photos"
              title={isMob ? undefined : "Nebo fotky přetáhni kamkoli do detailu (ze složky, e-mailu, WhatsAppu), případně vlož Cmd+V"}
              className="inline-flex items-center gap-1.5 text-[13px] font-bold text-muted-foreground hover:text-brand disabled:opacity-60">
              {uploading ? <Loader2 size={14} className="animate-spin motion-reduce:animate-none" /> : <Camera size={14} />}
              {uploading && upProg ? `Nahrávám ${Math.min(upProg.done + 1, upProg.total)} z ${upProg.total}…` : "Přidat fotky"}
            </button>
          )}
        </div>
      )}

      {attached.length > 0 && (
        <section className="pt-4 mt-4 border-t border-border/60" data-testid="task-photos">
          <div className="flex items-center gap-2.5 mb-2">
            <h3 className="text-[15px] font-bold">Fotky</h3>
            <span className="rounded-full bg-secondary text-muted-foreground px-2.5 py-0.5 text-xs font-bold tabular-nums">{attached.length}</span>
            {canAttach && !canPin && <span className="hidden md:inline ml-auto text-xs text-muted-foreground">další přetáhni sem nebo vlož <Kbd>⌘</Kbd><Kbd>V</Kbd></span>}
            {canPin && !done && (
              <button type="button" onClick={() => openViewer(viewPhotos, 0)} data-testid="defects-pin-open" title="Špendlíkem označ problém — vznikne z něj úkol na opravu"
                className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-brand/40 text-brand text-[13px] font-extrabold hover:bg-brand/10">
                <MapPin size={15} /> Označit závadu</button>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {attached.map((u, i) => (
              <div key={u} className="relative">
                <img src={authFileUrl(u, variantWidth(160))} alt="foto" loading="lazy" decoding="async" onClick={() => openViewer(viewPhotos, Math.max(0, viewPhotos.indexOf(u)))}
                  className="w-full aspect-square object-cover rounded-[12px] border border-border cursor-zoom-in hover:opacity-90" data-testid="task-photo" />
                {drafts.some((d) => d.photo_url === u) && (
                  <span className="absolute left-1.5 bottom-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full bg-brand text-white text-[11px] font-extrabold grid place-items-center border-2 border-white pointer-events-none">
                    {drafts.filter((d) => d.photo_url === u).length}</span>
                )}
                {canAttach && <button type="button" onClick={() => removePhoto(u)} aria-label="Odebrat fotku" title="Odebrat fotku" data-testid="task-photo-remove"
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/55 hover:bg-destructive text-white grid place-items-center backdrop-blur-sm"><X size={14} /></button>}
              </div>
            ))}
            {canAttach && <button type="button" onClick={photoPick.open} disabled={uploading} data-testid="add-task-photos" aria-label="Přidat fotky" title="Přidat fotky"
              className="aspect-square rounded-[12px] border-[1.5px] border-dashed border-border grid place-items-center text-muted-foreground hover:border-brand hover:text-brand">
              {uploading ? <Loader2 size={20} className="animate-spin motion-reduce:animate-none" /> : <ImagePlus size={20} />}
            </button>}
          </div>
          {uploading && upProg && !completeOpen && (
            <p className="mt-2 text-[12.5px] font-semibold text-brand tabular-nums">Nahrávám fotku {Math.min(upProg.done + 1, upProg.total)} z {upProg.total}…</p>
          )}
        </section>
      )}

      {/* fotky a popis dokončení */}
      {task.completion ? (
        <section className="pt-4 mt-4 border-t border-border/60" data-testid="completion-block">
          <div className="flex items-center gap-2.5 mb-2">
            <h3 className="text-[15px] font-bold">{donePhotos.length ? "Fotky" : "Dokončení"}</h3>
            {donePhotos.length > 0 && <span className="rounded-full bg-secondary text-muted-foreground px-2.5 py-0.5 text-xs font-bold tabular-nums">{donePhotos.length}</span>}
            {canPin && done && reviewPhotos.length > 0 && (
              <button type="button" onClick={() => openViewer(pinPhotos, 0)} data-testid="defects-pin-open"
                className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-brand/40 text-brand text-[13px] font-extrabold hover:bg-brand/10">
                <MapPin size={15} /> Označit závadu</button>
            )}
          </div>
          {task.completion.note && <p className="text-sm leading-relaxed whitespace-pre-wrap">{task.completion.note}</p>}
          <p className="text-xs text-muted-foreground mt-1">{task.completion.by_name} · {formatDateTime(task.completion.at)}{task.status === "Hotovo" ? " · čeká na schválení" : ""}</p>
          {donePhotos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
              {donePhotos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={authFileUrl(p, variantWidth(160))} alt="foto" loading="lazy" decoding="async" onClick={() => openViewer(viewPhotos, viewPhotos.indexOf(p))} className="w-full aspect-square object-cover rounded-[12px] border border-border cursor-zoom-in hover:opacity-90" data-testid="completion-photo" />
                  {[...defects, ...drafts].filter((d) => d.photo_url === p).length > 0 && (
                    <span className="absolute right-1.5 bottom-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full bg-brand text-white text-[11px] font-extrabold grid place-items-center border-2 border-white pointer-events-none">
                      {[...defects, ...drafts].filter((d) => d.photo_url === p).length}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      ) : null}

      {canPin && mode === "new" && drafts.length > 0 && (
        <div className="space-y-1.5">
          <DefectTaskForm ref={defRef} drafts={drafts} setDrafts={setDrafts} highlight={defFlash}
            projectId={task.project_id} projectName={task.project_name} sourceKind="task" sourceId={task.id}
            assignee={task.completion?.by ? { id: task.completion.by, name: task.completion.by_name } : assigneeUsers[0]}
            onEditDraft={showDraft} onCreated={() => { setDefMode(null); changed(); }}
            note={showApproval ? "Tento úkol pak můžete schválit — opravy půjdou v novém úkolu." : "Úkol dostane fotky se špendlíky a popisy."} />
          {showApproval && <button type="button" onClick={() => setDefMode("return")} data-testid="defects-back-to-return"
            className="text-[13px] font-bold text-brand hover:underline px-1">← Raději vrátit k opravě v tomto úkolu</button>}
        </div>
      )}

      <TaskDefects task={task} defects={defects} drafts={canPin ? retDrafts : []} setDrafts={setDrafts}
        canDecide={showApproval} canFix={isAssignee && !done} onShow={showDefect} onShowDraft={showDraft} onChanged={changed} onZoom={(u) => openViewer(viewPhotos, Math.max(0, viewPhotos.indexOf(u)))} />

      <TaskActivity task={task} user={user} createdByName={createdByName} comment={comment} setComment={setComment} picked={picked} setPicked={setPicked}
        onSend={() => guardCmt(addComment)} busy={cmtBusy} loadPeople={loadMentionable} />

      {/* Hlavní akce úplně dole (telefon, nebo co hlavička panelu neumí) — pracovník nejdřív projde zadání, fotky i konverzaci. */}
      {(showComplete || showApproval) && !headerHandles && <div ref={actionsRef} className={`mt-5 rounded-[16px] transition-shadow motion-reduce:transition-none ${defFlash && mode === "return" ? "shadow-[0_0_0_4px_rgba(224,112,63,.3)]" : ""}`}>{primaryActions(true)}</div>}

      {canDelete && (
        <div className="flex justify-end px-1 pt-4 pb-1">
          <button onClick={remove} data-testid="delete-task-btn" className="text-[12.5px] font-bold text-destructive hover:underline whitespace-nowrap">Smazat úkol</button>
        </div>
      )}

      <PhotoPinViewer open={!!viewer} onClose={() => setViewer(null)} photos={viewer?.photos || []} start={viewer?.start || 0}
        focusDraft={viewer?.focusDraft ?? null} title={task.title}
        fixedPins={defects} drafts={canPin ? drafts : []} setDrafts={setDrafts} editable={canPin} info={photoMeta}
        onDone={pinDone} />

      {/* Complete dialog */}
      <Dialog open={completeOpen} onOpenChange={(o) => { setCompleteOpen(o); if (!o && openedForComplete.current) onClose?.(); }}>
        <DialogContent sheet className="max-w-lg max-md:pt-8 overflow-y-auto md:max-h-[92vh]" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="text-left pr-10"><DialogTitle>Dokončení úkolu</DialogTitle></DialogHeader>
          <div className="relative space-y-3" onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); guardCpl(submitComplete); } }}
            onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragOver(true); }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => { dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragOver(false); } }}
            onDrop={(e) => { e.preventDefault(); dragDepth.current = 0; setDragOver(false); addFiles(e.dataTransfer?.files); }}>
            {dragOver && (
              <div className="absolute -inset-2 z-10 rounded-[14px] border-[3px] border-dashed border-brand bg-background/95 grid place-items-center pointer-events-none" data-testid="complete-dropzone">
                <div className="flex flex-col items-center gap-2.5 text-brand font-extrabold text-lg text-center px-4"><ImagePlus size={44} /> Pusťte fotky sem</div>
              </div>
            )}
            <div><label className="text-sm font-semibold">Co jste udělal?{!closesNow && " *"}</label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="complete-note" className="mt-1" />
              <p className="text-[13px] text-muted-foreground mt-1">{closesNow ? "Nepovinné — úkol se zavře rovnou." : "Uvidí ho ten, kdo úkol schvaluje."}</p></div>
            <div>
              {/* Jedno tlačítko: iOS nativní nabídka, Android Vyfotit / Galerie / Soubor, PC výběr souboru. */}
              {photoPick.inputs}
              <PhotoUploadButton onClick={photoPick.open} busy={uploading} dragging={dragOver} testid="add-photo-btn"
                hint={<span className="hidden md:inline">…nebo je sem přetáhněte či vložte přes Cmd+V</span>} />
              {uploading && upProg && (
                <div className="mt-2.5 rounded-[14px] border border-brand/30 bg-brand/5 p-3.5" data-testid="upload-progress">
                  <div className="flex items-center gap-2 text-sm font-bold text-brand">
                    <Loader2 size={17} className="animate-spin motion-reduce:animate-none" /> Nahrávám fotku {Math.min(upProg.done + 1, upProg.total)} z {upProg.total}
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden mt-2.5">
                    <div className="h-full bg-brand transition-all" style={{ width: `${Math.round((upProg.done / Math.max(upProg.total, 1)) * 100)}%` }} />
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-1.5">Náhledy naskakují postupně — nezavírej okno.</p>
                </div>
              )}
              {/* 363: i fotky, které už leží v detailu úkolu — odejdou s dokončením */}
              {(attached.length > 0 || photos.length > 0) && (
                <div className="flex gap-2 flex-wrap mt-3" data-testid="complete-photos">
                  {[...attached.map((u) => ({ u, inTask: true })), ...photos.map((u) => ({ u }))].map(({ u, inTask }) => (
                    <div key={u} className="relative">
                      <img src={authFileUrl(u, variantWidth(80))} alt="" decoding="async" className="w-20 h-20 object-cover rounded-[10px] border border-border" />
                      <button onClick={() => (inTask ? removePhoto(u) : setPhotos(photos.filter((x) => x !== u)))} aria-label="Odebrat fotku" className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex md:justify-end"><Button onClick={() => guardCpl(submitComplete)} disabled={cplBusy || uploading} data-testid="complete-confirm" className="w-full md:w-auto h-[52px] md:h-12 rounded-full font-extrabold text-[15px] px-8">{cplBusy ? (closesNow ? "Ukládám…" : "Odesílám…") : (closesNow ? "Označit jako hotové" : "Odeslat ke schválení")}</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return dialog */}
      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent >
          <DialogHeader><DialogTitle>Vrátit k přepracování</DialogTitle></DialogHeader>
          <Textarea value={returnText} onChange={(e) => setReturnText(e.target.value)} placeholder="Důvod vrácení…" rows={3} data-testid="return-note"
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); doReturn(); } }} />
          <Button onClick={doReturn} data-testid="return-confirm" className="w-full h-12 font-bold">Vrátit úkol</Button>
        </DialogContent>
      </Dialog>

    </div>
  );
}
